import type {
  StormMotion,
  Alert,
  AlertEvaluation,
  AlertPolicy,
  AlertSoundPolicy,
  ClosureCondition,
  ClosureGeometry,
  ClosureReason,
  AreaGeometry,
  AreaKind,
  Asset,
  AssetRuleOverrides,
  AssetType,
  AuditEvent,
  ComponentHealth,
  DashboardSummary,
  GeoPoint,
  Me,
  NotificationGroup,
  NotificationLog,
  LightningFlash,
  PublicAlertView,
  PublicMapLink,
  PublicMapView,
  RoadClosure,
  Role,
  SmsProvider,
  SystemHealth,
  Tenant,
  TenantAlert,
  TenantArea,
  TenantIssue,
  TenantSmsSettings,
  ToneDef,
  User,
  WatchArea,
  ListhubList,
  TenantListhubSettings,
} from '@ww/shared';

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(message: string, status: number, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

/* ---------- acting tenant (super_admin tenant switcher) ---------- */

let actingTenantId: string | null = null;
const TENANT_KEY = 'ww.actingTenantId';
try {
  actingTenantId = window.sessionStorage.getItem(TENANT_KEY);
} catch {
  actingTenantId = null;
}

export function getActingTenantId(): string | null {
  return actingTenantId;
}
export function setActingTenantId(id: string | null): void {
  actingTenantId = id;
  try {
    if (id) window.sessionStorage.setItem(TENANT_KEY, id);
    else window.sessionStorage.removeItem(TENANT_KEY);
  } catch {
    /* storage unavailable */
  }
}

/** Append `tenantId=` for the acting tenant (if any), respecting an existing query string. */
export function withTenant(path: string): string {
  if (!actingTenantId || /[?&]tenantId=/.test(path)) return path;
  const sep = path.includes('?') ? '&' : '?';
  return `${path}${sep}tenantId=${encodeURIComponent(actingTenantId)}`;
}

/* ---------- core request ---------- */

interface RequestOpts {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Skip the acting-tenant query param (auth + admin endpoints). */
  noTenant?: boolean;
  signal?: AbortSignal;
}

async function request<T>(path: string, opts: RequestOpts = {}): Promise<T> {
  const url = opts.noTenant ? path : withTenant(path);
  const headers: Record<string, string> = { Accept: 'application/json' };
  let body: string | undefined;
  if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(opts.body);
  }
  let res: Response;
  try {
    res = await fetch(url, {
      method: opts.method ?? 'GET',
      credentials: 'include',
      headers,
      body,
      signal: opts.signal,
    });
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') throw e;
    throw new ApiError('Network error: could not reach the WeatherWatch API', 0);
  }
  const text = res.status === 204 ? '' : await res.text();
  let data: unknown = undefined;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`.trim();
    if (data && typeof data === 'object') {
      const o = data as Record<string, unknown>;
      const m = o.message ?? o.error;
      if (typeof m === 'string' && m) msg = m;
    } else if (typeof data === 'string' && data && data.length < 200) {
      msg = data;
    }
    if (res.status === 429) msg = 'Too many attempts. Please wait a minute and try again.';
    throw new ApiError(msg, res.status, data);
  }
  return data as T;
}

/* ---------- response shapes not in @ww/shared ---------- */

export interface AlertDetailResponse {
  alert: Alert;
  evaluation: AlertEvaluation | null;
  assets: Asset[];
  notifications: NotificationLog[];
}

export interface AlertPolicyResponse {
  policy: AlertPolicy;
  notificationGroups: NotificationGroup[];
  globalDefaults: AlertPolicy;
}

/** What the browser sends; everything else on a closure is derived or from the session. */
export interface ClosureInput {
  roadName: string;
  notes: string;
  reason: ClosureReason;
  condition: ClosureCondition;
  geometry: ClosureGeometry;
  expectedReopenAt: string | null;
  longTerm: boolean;
  /**
   * Groups to text about this closure. Always sent, including as `[]` — the API reads
   * "none" literally, and omitting the field on an edit would be indistinguishable from
   * clearing it.
   */
  notifyGroupIds: string[];
}

export interface LightningResponse {
  flashes: LightningFlash[];
  /** Carried with the data so a client cannot draw the dots and forget the label. */
  source: string;
  caveat: string;
  retentionMinutes: number;
  status: { at: string; ok: boolean; filesFetched: number; detail: string | null } | null;
}

export interface AlertSoundsResponse {
  sounds: AlertSoundPolicy;
  /** The catalogue, served rather than hard-coded in the page, so adding a tone is one place. */
  tones: ToneDef[];
  globalDefaults: AlertSoundPolicy;
}

export interface RadarFrame {
  time: string;
  url: string;
}
/** Bulk precipitation motion + footprint, for weather with no NWS warning behind it. */
export interface RadarNowcast {
  motion: StormMotion | null;
  geometry: AreaGeometry | null;
  from: string | null;
  to: string | null;
  reason?: 'no-frames' | 'no-echo' | 'no-correlation' | 'implausible-speed' | 'upstream';
}

export interface PlaceHit {
  geoid: string;
  name: string;
  state: string;
  center: [number, number];
}

export interface RadarProviderOption {
  id: string;
  label: string;
}

export interface RadarProductOption {
  id: string;
  label: string;
  /** Categorical products must never be blurred; blending categories invents a third. */
  categorical: boolean;
}

export interface RadarFramesResponse {
  provider: string;
  /** Providers a viewer may choose between. */
  providers?: RadarProviderOption[];
  /** Credit line for the source, shown in the map's attribution control. */
  attribution?: string;
  product?: string;
  products?: RadarProductOption[];
  tileSize?: number;
  /** Highest zoom the source has real data for; above it MapLibre should upscale. */
  maxZoom?: number;
  frames: RadarFrame[];
  status: ComponentHealth;
}

export interface SmsTestResponse {
  result: string;
  detail: string;
  provider?: SmsProvider;
  /** Who the test was actually sent to, so the operator knows which phone to check. */
  recipients?: string[];
}

export interface SmsSettingsResponse {
  sms: TenantSmsSettings;
}

/**
 * `apiKey` is three-way on purpose: leave it out to keep the stored key, pass null to
 * clear it, pass a string to replace it. The form never holds the real key.
 */
export interface SmsSettingsInput {
  provider?: SmsProvider;
  baseUrl?: string;
  apiKey?: string | null;
}

export interface AssetInput {
  name?: string;
  type?: AssetType;
  lat?: number;
  lon?: number;
  location?: GeoPoint;
  description?: string;
  tags?: string[];
  alertRadiusMiles?: number;
  ruleOverrides?: AssetRuleOverrides;
  notificationGroupIds?: string[];
  enabled?: boolean;
  /** Publish this asset's name, type and point on the county's public map. */
  publicMap?: boolean;
}

export interface TenantInput {
  name: string;
  slug: string;
  state: string;
  countyName: string;
  zoneCodes: string[];
  center: [number, number];
  timezone: string;
  boundary?: AreaGeometry | null;
  area?: TenantArea | null;
  /** Install this tenant's own SMS gateway as part of creating it. */
  sms?: SmsSettingsInput;
  notificationGroups?: NotificationGroup[];
  /** Optional first login, created together with the tenant. */
  firstAdmin?: { name: string; username: string; email: string; password: string };
  status?: Tenant['status'];
}

export interface CreateTenantResponse {
  tenant: Tenant;
  user: User | null;
  issues: TenantIssue[];
}

export interface NwsZone {
  id: string;
  type: 'county' | 'public' | 'fire';
  name: string;
  state: string;
}

export interface ZoneDerivation {
  zoneCodes: string[];
  zones: NwsZone[];
  timezone: string | null;
  source: 'name+point' | 'name' | 'point' | 'none';
  notes: string[];
}

export interface TenantSummary {
  tenant: Tenant;
  counts: {
    users: number;
    activeUsers: number;
    assets: number;
    enabledAssets: number;
    notificationGroups: number;
    recipients: number;
    activeAlerts: number;
    notifications24h: number;
    failedNotifications24h: number;
  };
  /** Masked gateway view: provider, whether a key is installed, when it last worked. */
  sms?: TenantSmsSettings;
  issues: TenantIssue[];
  lastActivityAt: string | null;
}

export interface CoverageSettings {
  area: TenantArea | null;
  boundary: AreaGeometry | null;
  center: [number, number];
  state: string;
  countyName: string;
  zoneCodes?: string[];
}

/** A county or place returned by the Census area search. */
export interface AreaMatch {
  kind: Exclude<AreaKind, 'custom'>;
  geoid: string;
  name: string;
  state: string;
}

export interface AreaDetail extends AreaMatch {
  boundary: AreaGeometry;
  center: [number, number];
}

export interface UserInput {
  name: string;
  username: string;
  email: string;
  role: Role;
  password: string;
  tenantId: string | null;
}

export interface UserPatch {
  name?: string;
  email?: string;
  role?: Role;
  password?: string;
  status?: User['status'];
}

const enc = encodeURIComponent;

/* ---------- endpoints ---------- */

export const api = {
  // auth
  login: (username: string, password: string) =>
    request<Me>('/api/auth/login', { method: 'POST', body: { username, password }, noTenant: true }),
  logout: () => request<{ ok: boolean }>('/api/auth/logout', { method: 'POST', noTenant: true }),
  me: () => request<Me>('/api/auth/me', { noTenant: true }),

  // dashboard + alerts
  dashboard: () => request<DashboardSummary>('/api/dashboard'),
  activeAlerts: () => request<TenantAlert[]>('/api/alerts/active'),
  alertHistory: (limit = 50) => request<TenantAlert[]>(`/api/alerts/history?limit=${limit}`),
  /** `nwsId` is the raw (decoded) NWS id; it is percent-encoded exactly once here. */
  alert: (nwsId: string) => request<AlertDetailResponse>(`/api/alerts/${enc(nwsId)}`),
  /** Read-only view behind an SMS short link. No session required, so no acting tenant. */
  publicAlert: (code: string) => request<PublicAlertView>(`/api/public/alerts/${enc(code)}`, { noTenant: true }),
  /** The county's public map behind a link code. No session either, so no acting tenant. */
  publicMap: (code: string) => request<PublicMapView>(`/api/public/map/${enc(code)}`, { noTenant: true }),

  // assets
  assets: () => request<Asset[]>('/api/assets'),
  createAsset: (body: AssetInput) => request<Asset>('/api/assets', { method: 'POST', body }),
  updateAsset: (id: string, body: AssetInput) => request<Asset>(`/api/assets/${enc(id)}`, { method: 'PATCH', body }),
  deleteAsset: (id: string) => request<void>(`/api/assets/${enc(id)}`, { method: 'DELETE' }),

  // road closures
  closures: (includeEnded = false) =>
    request<RoadClosure[]>(`/api/closures${includeEnded ? '?include=ended' : ''}`),
  createClosure: (body: ClosureInput) => request<RoadClosure>('/api/closures', { method: 'POST', body }),
  updateClosure: (id: string, body: Partial<ClosureInput>) =>
    request<RoadClosure>(`/api/closures/${enc(id)}`, { method: 'PATCH', body }),
  /** The roadside action: one call, its own audit line. */
  reopenClosure: (id: string) => request<RoadClosure>(`/api/closures/${enc(id)}/reopen`, { method: 'POST' }),
  deleteClosure: (id: string) => request<void>(`/api/closures/${enc(id)}`, { method: 'DELETE' }),

  // settings
  alertPolicy: () => request<AlertPolicyResponse>('/api/settings/alert-policy'),
  updateAlertPolicy: (body: { policy?: Partial<AlertPolicy>; notificationGroups?: NotificationGroup[] }) =>
    request<AlertPolicyResponse>('/api/settings/alert-policy', { method: 'PATCH', body }),

  lightning: () => request<LightningResponse>('/api/lightning'),

  // public map links
  publicMapLinks: () => request<{ links: PublicMapLink[] }>('/api/settings/public-map'),
  createPublicMapLink: () => request<PublicMapLink>('/api/settings/public-map', { method: 'POST', body: {} }),
  revokePublicMapLink: (code: string) =>
    request<void>(`/api/settings/public-map/${enc(code)}`, { method: 'DELETE' }),

  alertSounds: () => request<AlertSoundsResponse>('/api/settings/sounds'),
  updateAlertSounds: (body: Partial<AlertSoundPolicy>) =>
    request<AlertSoundsResponse>('/api/settings/sounds', { method: 'PATCH', body }),

  // system
  health: () => request<SystemHealth>('/api/system/health'),
  notifications: (limit = 50) => request<NotificationLog[]>(`/api/notifications?limit=${limit}`),
  audit: (limit = 100) => request<AuditEvent[]>(`/api/audit?limit=${limit}`),
  smsTest: (to?: string) =>
    request<SmsTestResponse>('/api/integrations/sms/test', { method: 'POST', body: to ? { to } : {} }),

  // per-tenant SMS gateway
  smsSettings: () => request<SmsSettingsResponse>('/api/settings/sms'),
  updateSmsSettings: (body: SmsSettingsInput) =>
    request<SmsSettingsResponse>('/api/settings/sms', { method: 'PATCH', body }),

  // ListHub: emails alerts and road closures to one of the tenant's ListHub lists
  listhub: () => request<TenantListhubSettings>('/api/settings/listhub'),
  setListhubKey: (apiKey: string) =>
    request<TenantListhubSettings>('/api/settings/listhub/key', { method: 'PUT', body: { apiKey } }),
  updateListhub: (body: { listId?: string; sendAlerts?: boolean; sendClosures?: boolean }) =>
    request<TenantListhubSettings>('/api/settings/listhub', { method: 'PATCH', body }),
  disconnectListhub: () => request<TenantListhubSettings>('/api/settings/listhub', { method: 'DELETE' }),
  listhubLists: () => request<{ lists: ListhubList[] }>('/api/settings/listhub/lists'),
  testListhub: () => request<{ ok: boolean; detail: string }>('/api/settings/listhub/test', { method: 'POST' }),

  // radar
  /** Incorporated places intersecting a box, for naming what a storm path crosses. */
  geoPlaces: (bbox: [number, number, number, number]) =>
    request<{ places: PlaceHit[] }>(`/api/geo/places?bbox=${bbox.map((n) => n.toFixed(3)).join(',')}`),

  radarFrames: (provider?: string | null, product?: string | null) => {
    const q = new URLSearchParams();
    if (provider) q.set('provider', provider);
    if (product) q.set('product', product);
    const qs = q.toString();
    return request<RadarFramesResponse>(`/api/radar/frames${qs ? `?${qs}` : ''}`);
  },
  radarNowcast: (bbox: [number, number, number, number]) =>
    request<RadarNowcast>(`/api/radar/nowcast?bbox=${bbox.map((n) => n.toFixed(3)).join(',')}`),

  // admin
  tenants: () => request<Tenant[]>('/api/admin/tenants', { noTenant: true }),
  createTenant: (body: TenantInput) =>
    request<CreateTenantResponse>('/api/admin/tenants', { method: 'POST', body, noTenant: true }),
  tenant: (id: string) => request<Tenant>(`/api/admin/tenants/${enc(id)}`, { noTenant: true }),
  /** Permanent. `confirm` must equal the tenant's slug. */
  deleteTenant: (id: string, confirm: string) =>
    request<{ deleted: boolean; removed: Record<string, number> }>(
      `/api/admin/tenants/${enc(id)}?confirm=${enc(confirm)}`,
      { method: 'DELETE', noTenant: true },
    ),
  tenantSummary: (id: string) =>
    request<TenantSummary>(`/api/admin/tenants/${enc(id)}/summary`, { noTenant: true }),
  deriveZones: (p: { state: string; kind: string; name?: string; lon: number; lat: number }) =>
    request<ZoneDerivation>(
      `/api/geo/zones?state=${enc(p.state)}&kind=${enc(p.kind)}&name=${enc(p.name ?? '')}&lon=${p.lon}&lat=${p.lat}`,
      { noTenant: true },
    ),

  // Read another tenant's data without switching the acting tenant.
  assetsFor: (id: string) => request<Asset[]>(`/api/assets?tenantId=${enc(id)}`),
  auditFor: (id: string, limit = 25) =>
    request<AuditEvent[]>(`/api/audit?tenantId=${enc(id)}&limit=${limit}`),

  // tenant's own coverage area (scoped server-side to the caller's tenant)
  coverage: () => request<CoverageSettings>('/api/settings/coverage'),
  updateCoverage: (body: { boundary: AreaGeometry; area: TenantArea }) =>
    request<CoverageSettings>('/api/settings/coverage', { method: 'PATCH', body }),

  // extra alert locations. `tenantId` lets a super-admin act on a tenant other than the
  // acting one (Tenant Detail); without it the acting tenant is used.
  watchAreas: (tenantId?: string) =>
    request<{ watchAreas: WatchArea[] }>(watchAreaPath('', tenantId), { noTenant: !!tenantId }),
  addWatchArea: (body: { kind: 'county' | 'city'; geoid: string; sendSms: boolean }, tenantId?: string) =>
    request<WatchArea>(watchAreaPath('', tenantId), { method: 'POST', body, noTenant: !!tenantId }),
  updateWatchArea: (id: string, body: { sendSms: boolean }, tenantId?: string) =>
    request<WatchArea>(watchAreaPath(`/${enc(id)}`, tenantId), { method: 'PATCH', body, noTenant: !!tenantId }),
  deleteWatchArea: (id: string, tenantId?: string) =>
    request<void>(watchAreaPath(`/${enc(id)}`, tenantId), { method: 'DELETE', noTenant: !!tenantId }),

  // coverage-area lookup (Census TIGERweb, proxied by the API)
  searchAreas: (kind: AreaMatch['kind'], state: string, q: string) =>
    request<{ areas: AreaMatch[] }>(
      `/api/geo/areas?kind=${enc(kind)}&state=${enc(state)}&q=${enc(q)}`,
      { noTenant: true },
    ),
  areaDetail: (kind: AreaMatch['kind'], geoid: string) =>
    request<AreaDetail>(`/api/geo/areas/${enc(kind)}/${enc(geoid)}`, { noTenant: true }),
  updateTenant: (id: string, body: Partial<TenantInput>) =>
    request<Tenant>(`/api/admin/tenants/${enc(id)}`, { method: 'PATCH', body, noTenant: true }),
  users: (tenantId?: string | null) =>
    request<User[]>(tenantId ? `/api/admin/users?tenantId=${enc(tenantId)}` : '/api/admin/users', { noTenant: true }),
  createUser: (body: UserInput) => request<User>('/api/admin/users', { method: 'POST', body, noTenant: true }),
  updateUser: (id: string, body: UserPatch) =>
    request<User>(`/api/admin/users/${enc(id)}`, { method: 'PATCH', body, noTenant: true }),
  deleteUser: (id: string) => request<void>(`/api/admin/users/${enc(id)}`, { method: 'DELETE', noTenant: true }),
};

function watchAreaPath(suffix: string, tenantId?: string): string {
  const path = `/api/settings/watch-areas${suffix}`;
  return tenantId ? `${path}?tenantId=${enc(tenantId)}` : path;
}

export function eventsUrl(): string {
  return withTenant('/api/events');
}

/** Absolute URL for a same-origin path, keeping `{z}/{x}/{y}` template braces intact. */
export function absoluteUrl(url: string): string {
  if (/^https?:\/\//i.test(url)) return url;
  return window.location.origin + (url.startsWith('/') ? url : `/${url}`);
}
